import React from "react";
import {
  Box,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { Logout } from "@mui/icons-material";
import Avatar from "../atoms/Avatar";

const DRAWER_WIDTH = 210;
const MINI_DRAWER_WIDTH = 80;

interface SidebarProps {
  isMobile: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (val: boolean) => void;
  roleConfig: any;
  navItems: any[];
  currentPage: string;
  handleNav: (key: string) => void;
  handleLogout: () => void;
  user: any;
  displayName: string;
  displayDept: string;
  displayInitials: string;
  displayColor: string;
  pendingLeavesCount: number;
  totalUnreadChat?: number;
  profilePic?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  isMobile,
  sidebarOpen,
  setSidebarOpen,
  roleConfig,
  navItems,
  currentPage,
  handleNav,
  handleLogout,
  user,
  displayName,
  displayDept,
  displayInitials,
  displayColor,
  pendingLeavesCount,
  totalUnreadChat = 0,
  profilePic,
}) => {
  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#ffffff",
        overflowX: "hidden",
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 2.5,
          borderBottom: "1px solid rgba(226,232,240,0.6)",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            background: "linear-gradient(135deg, #85D1DB, #B6F2D1)",
            boxShadow: "0 4px 12px rgba(133,209,219,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            fontWeight: 800,
            color: "#0f172a",
            flexShrink: 0,
          }}
        >
          HR
        </Box>
        <Box
          sx={{
            opacity: sidebarOpen || isMobile ? 1 : 0,
            transition: "opacity 0.2s",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 800,
              letterSpacing: "-0.03em",
              color: "#0f172a",
              lineHeight: 1.2,
            }}
          >
            HRCore
          </Typography>
          <Typography
            sx={{
              fontSize: 10,
              color: "#64748b",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {roleConfig?.label}
          </Typography>
        </Box>
      </Box>

      {/* Nav */}
      <List
        sx={{
          flex: 1,
          py: 1.2,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            background: "#e2e8f0",
            borderRadius: 99,
          },
        }}
      >
        {navItems.map((groupObj: any, gIdx: number) => (
          <React.Fragment key={groupObj.group}>
            {/* Group Header */}
            {groupObj.group !== "Overview" && groupObj.group !== "Other" && (
              <Box>
                <Typography
                  sx={{
                    px: 3.5,
                    pt: gIdx > 0 ? 2 : 1,
                    pb: 1,
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    opacity: sidebarOpen || isMobile ? 1 : 0,
                    transition: "all 0.2s ease",
                    maxHeight: sidebarOpen || isMobile ? 40 : 0,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  {groupObj.group}
                </Typography>
                {/* Divider for Mini mode instead of header */}
                {!sidebarOpen && !isMobile && gIdx > 0 && (
                  <Box
                    sx={{
                      my: 1,
                      mx: 2.5,
                      height: 1,
                      background: "rgba(226,232,240,0.8)",
                    }}
                  />
                )}
              </Box>
            )}

            {groupObj.items.map((item: any) => {
              const active = currentPage === item.key;
              return (
                <Tooltip
                  title={item.label}
                  placement="right"
                  key={item.key}
                  disableHoverListener={sidebarOpen || isMobile}
                  arrow
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, 14],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <ListItem
                    onClick={() => handleNav(item.key)}
                    sx={{
                      py: 1.2,
                      px: 2,
                      mx: 1.5,
                      mb: 0.5,
                      borderRadius: 2.5,
                      cursor: "pointer",
                      background: active
                        ? "linear-gradient(90deg, rgba(133,209,219,0.15), rgba(133,209,219,0.05))"
                        : "transparent",
                      color: active ? "#0f172a" : "#64748b",
                      position: "relative",

                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&::before": active
                        ? {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            height: "60%",
                            width: 4,
                            borderRadius: "0 4px 4px 0",
                            background: "#85D1DB",
                          }
                        : {},
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: active ? "#85D1DB" : "inherit",
                        transition: "color 0.2s ease",
                      }}
                    >
                      {React.cloneElement(item.icon as React.ReactElement, {
                        fontSize: "small",
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      sx={{
                        margin: 0,
                        opacity: sidebarOpen || isMobile ? 1 : 0,
                        transition: "opacity 0.2s ease",
                        whiteSpace: "nowrap",
                        "& .MuiListItemText-primary": {
                          fontSize: 13,
                          fontWeight: active ? 600 : 500,
                        },
                      }}
                    />
                    {item.key === "leave" && pendingLeavesCount > 0 && (
                      <Box
                        sx={{
                          background: "#dc2626",
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: 700,
                          px: 0.8,
                          borderRadius: 99,
                          lineHeight: 1.6,
                          opacity: sidebarOpen || isMobile ? 1 : 0,
                          transition: "opacity 0.2s ease",
                        }}
                      >
                        {pendingLeavesCount}
                      </Box>
                    )}
                    {item.key === "chat" && totalUnreadChat > 0 && (
                      <Box
                        sx={{
                          background: "#ef4444",
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: 700,
                          px: 0.8,
                          borderRadius: 99,
                          lineHeight: 1.6,
                          opacity: sidebarOpen || isMobile ? 1 : 0,
                          transition: "opacity 0.2s ease",
                        }}
                      >
                        {totalUnreadChat}
                      </Box>
                    )}
                  </ListItem>
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </List>

      {/* User footer */}
      <Box sx={{ borderTop: "1px solid #e5e7eb", p: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            p: 1.2,
            borderRadius: 2,
            background: "#f1f5f9",
            mb: 1,
            cursor: "pointer",

          }}
          onClick={() => handleNav("myprofile")}
        >
          {user && (
            <Avatar
              initials={displayInitials}
              color={displayColor}
              size={32}
              src={profilePic}
            />
          )}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              opacity: sidebarOpen || isMobile ? 1 : 0,
              transition: "opacity 0.2s ease",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 500,
                color: "#0f172a",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{
                fontSize: 9,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                whiteSpace: "normal",
                lineHeight: 1.2,
                mt: 0.2,
              }}
            >
              {displayDept}
            </Typography>
          </Box>
        </Box>
        <Box
          onClick={handleLogout}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarOpen || isMobile ? "flex-start" : "center",
            gap: 1,
            px: 1.2,
            py: 0.8,
            borderRadius: 1.5,
            border: "1px solid #d1d5db",
            cursor: "pointer",
            fontSize: 11,
            color: "#64748b",
            transition: "all 0.15s",

          }}
        >
          <Logout sx={{ fontSize: 16 }} />
          {(sidebarOpen || isMobile) && (
            <span style={{ whiteSpace: "nowrap" }}>Sign Out</span>
          )}
        </Box>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: sidebarOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
        flexShrink: 0,
        whiteSpace: "nowrap",
        transition: "width 0.2s ease-in-out",
        "& .MuiDrawer-paper": {
          width: sidebarOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
          border: "none",
          borderRight: "1px solid rgba(226,232,240,0.8)",
          boxShadow: "none",
          overflowX: "hidden",
          transition: "width 0.2s ease-in-out",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
